import { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import MapTouchGate from '../components/map/MapTouchGate';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import {
  Activity,
  Clock,
  LogOut,
  CalendarOff,
  Users,
  RefreshCw,
  MapPin,
  X,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Pencil,
  StickyNote,
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { formatTimeInTz } from '@/utils/timezone';
import apiService from '../services/api';
import { useAppRefresh } from '../hooks/useAppRefresh';
import type { Numeric } from '@/utils/numeric';
import { toNum } from '@/utils/numeric';

// Fix Leaflet default marker icons (bundler issue)
delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const clockInIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const clockOutIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

interface Session {
  id: number;
  clock_in_time: string;
  clock_out_time: string | null;
  total_hours: string | null;
  status: string;
  edited: boolean;
  edit_reason: string | null;
  notes: string | null;
  clock_in_latitude: Numeric;
  clock_in_longitude: Numeric;
  clock_out_latitude: Numeric;
  clock_out_longitude: Numeric;
}

interface EmployeeStatus {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  // Timesheet
  first_clock_in: string | null;
  last_clock_in: string | null;
  last_clock_out: string | null;
  total_hours_today: string | null;
  is_clocked_in: boolean;
  session_count: number;
  sessions: Session[] | null;
  clock_in_latitude: Numeric;
  clock_in_longitude: Numeric;
  clock_out_latitude: Numeric;
  clock_out_longitude: Numeric;
  active_clock_in_latitude: Numeric;
  active_clock_in_longitude: Numeric;
  // Leave
  leave_id: number | null;
  leave_type: string | null;
  leave_reason: string | null;
}

interface DashboardMeta {
  today: string;
  timezone: string;
  is_hr_view: boolean;
}

type LocationMapTarget = {
  name: string;
  clockIn: [number, number] | null;
  clockOut: [number, number] | null;
};

const LEAVE_LABELS: Record<string, string> = {
  annual: 'Annual Leave',
  personal: 'Personal/Sick Leave',
  unpaid: 'Unpaid Leave',
  other: 'Other Leave',
  time_in_lieu: 'Time in Lieu',
  long_service: 'Long Service Leave',
};

function getEmployeeStatus(emp: EmployeeStatus): 'clocked_in' | 'clocked_out' | 'on_leave' | 'not_started' {
  if (emp.leave_id) return 'on_leave';
  if (emp.is_clocked_in) return 'clocked_in';
  if (emp.first_clock_in) return 'clocked_out';
  return 'not_started';
}

function StatusBadge({ status }: { status: ReturnType<typeof getEmployeeStatus> }) {
  const map = {
    clocked_in: { label: 'Clocked In', cls: 'bg-emerald-100 text-emerald-800' },
    clocked_out: { label: 'Clocked Out', cls: 'bg-neutral-100 text-neutral-700' },
    on_leave: { label: 'On Leave', cls: 'bg-blue-100 text-blue-800' },
    not_started: { label: 'Not Started', cls: 'bg-yellow-50 text-yellow-700' },
  };
  const { label, cls } = map[status];
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
}

function formatTime(iso: string | null, timezone: string): string {
  if (!iso) return '—';
  try {
    return formatTimeInTz(iso, timezone);
  } catch {
    return '—';
  }
}

function formatHours(hours: string | null): string {
  if (!hours || parseFloat(hours) === 0) return '—';
  const h = parseFloat(hours);
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  if (mm === 0) return `${hh}h`;
  return `${hh}h ${mm}m`;
}


// ─────────────────────────────────────────────────────────────────────────────
// Location Map Modal
// ─────────────────────────────────────────────────────────────────────────────
function LocationModal({ target, onClose }: { target: LocationMapTarget; onClose: () => void }) {
  const center: [number, number] = target.clockIn ?? target.clockOut ?? [-33.8688, 151.2093];
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-safe overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden my-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 sticky top-0 bg-white z-10">
          <h3 className="font-semibold text-neutral-900 text-sm">
            Clock locations — {target.name}
          </h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 p-1">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="h-64 sm:h-72">
          <MapContainer
            center={center}
            zoom={15}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={false}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />
            {target.clockIn && (
              <Marker position={target.clockIn} icon={clockInIcon}>
                <Popup>Clock In</Popup>
              </Marker>
            )}
            {target.clockOut && (
              <Marker position={target.clockOut} icon={clockOutIcon}>
                <Popup>Clock Out</Popup>
              </Marker>
            )}
            <MapTouchGate />
          </MapContainer>
        </div>
        <div className="flex gap-4 px-4 py-2 border-t border-neutral-100 text-xs text-neutral-500">
          {target.clockIn && (
            <span className="flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
              Clock In
            </span>
          )}
          {target.clockOut && (
            <span className="flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
              Clock Out
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────
function formatSessionDuration(hours: string | null, clockIn: string, clockOut: string | null): string {
  if (clockOut == null) {
    const elapsedMs = Date.now() - new Date(clockIn).getTime();
    const h = elapsedMs / (1000 * 60 * 60);
    return formatHours(h.toFixed(2)) + ' so far';
  }
  return formatHours(hours);
}

function HRLiveDashboard() {
  const [employees, setEmployees] = useState<EmployeeStatus[]>([]);
  const [meta, setMeta] = useState<DashboardMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [locationTarget, setLocationTarget] = useState<LocationMapTarget | null>(null);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchDashboard = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      // apiService.get() returns response.data already, so resp = { success, data, meta }
      const resp = await apiService.get<EmployeeStatus[]>('/users/live-dashboard') as unknown as {
        success: boolean;
        data: EmployeeStatus[];
        meta: DashboardMeta;
      };
      setEmployees(Array.isArray(resp.data) ? resp.data : []);
      setMeta(resp.meta ?? null);
      setLastUpdated(new Date());
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load dashboard';
      setError(msg);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    intervalRef.current = setInterval(() => fetchDashboard(true), 30_000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchDashboard]);

  useAppRefresh(useCallback(() => { fetchDashboard(true); }, [fetchDashboard]));

  // ── Derived counts ──
  const counts = {
    total: employees.length,
    clockedIn: employees.filter(e => getEmployeeStatus(e) === 'clocked_in').length,
    clockedOut: employees.filter(e => getEmployeeStatus(e) === 'clocked_out').length,
    onLeave: employees.filter(e => getEmployeeStatus(e) === 'on_leave').length,
    notStarted: employees.filter(e => getEmployeeStatus(e) === 'not_started').length,
  };

  function openLocation(emp: EmployeeStatus) {
    // When currently clocked in, show the active session's clock-in location.
    // Otherwise show the first session's clock-in location.
    const clockInLat = emp.is_clocked_in
      ? (emp.active_clock_in_latitude ?? emp.clock_in_latitude)
      : emp.clock_in_latitude;
    const clockInLng = emp.is_clocked_in
      ? (emp.active_clock_in_longitude ?? emp.clock_in_longitude)
      : emp.clock_in_longitude;
    const inLat = toNum(clockInLat), inLng = toNum(clockInLng);
    const outLat = toNum(emp.clock_out_latitude), outLng = toNum(emp.clock_out_longitude);
    const clockIn: [number, number] | null =
      inLat != null && inLng != null ? [inLat, inLng] : null;
    const clockOut: [number, number] | null =
      outLat != null && outLng != null ? [outLat, outLng] : null;
    if (!clockIn && !clockOut) return;
    const displayName = emp.first_name || emp.last_name
      ? `${emp.first_name ?? ''} ${emp.last_name ?? ''}`.trim()
      : emp.email;
    setLocationTarget({ name: displayName, clockIn, clockOut });
  }

  function openSessionLocation(emp: EmployeeStatus, s: Session) {
    const inLat = toNum(s.clock_in_latitude), inLng = toNum(s.clock_in_longitude);
    const outLat = toNum(s.clock_out_latitude), outLng = toNum(s.clock_out_longitude);
    const clockIn: [number, number] | null =
      inLat != null && inLng != null
        ? [inLat, inLng]
        : null;
    const clockOut: [number, number] | null =
      outLat != null && outLng != null
        ? [outLat, outLng]
        : null;
    if (!clockIn && !clockOut) return;
    const displayName = emp.first_name || emp.last_name
      ? `${emp.first_name ?? ''} ${emp.last_name ?? ''}`.trim()
      : emp.email;
    setLocationTarget({ name: displayName, clockIn, clockOut });
  }

  function sessionHasLocation(s: Session): boolean {
    return (
      s.clock_in_latitude != null ||
      s.clock_out_latitude != null
    );
  }

  function hasLocation(emp: EmployeeStatus): boolean {
    return (
      emp.clock_in_latitude != null ||
      emp.active_clock_in_latitude != null ||
      emp.clock_out_latitude != null
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title={meta?.is_hr_view === false ? 'My Team — Live Dashboard' : 'Live Attendance Dashboard'}
        subtitle={
          meta
            ? `${meta.today} · ${meta.timezone}`
            : 'Real-time employee attendance for today'
        }
        icon={<Activity className="h-6 w-6 text-white" />}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* Last-updated indicator */}
        {lastUpdated && (
          <div className="flex items-center justify-end mb-6">
            <span className="text-xs text-neutral-400">
              Updated {formatTimeInTz(lastUpdated.toISOString(), meta?.timezone ?? 'Australia/Melbourne')}
            </span>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mb-4 flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Summary stats */}
        {!loading && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <StatCard icon={<Users className="h-5 w-5 text-neutral-500" />} label="Total" value={counts.total} color="neutral" />
            <StatCard icon={<Clock className="h-5 w-5 text-emerald-600" />} label="Clocked In" value={counts.clockedIn} color="emerald" />
            <StatCard icon={<LogOut className="h-5 w-5 text-neutral-500" />} label="Clocked Out" value={counts.clockedOut} color="neutral" />
            <StatCard icon={<CalendarOff className="h-5 w-5 text-blue-600" />} label="On Leave" value={counts.onLeave} color="blue" />
          </div>
        )}

        {/* Employee cards */}
        {loading ? (
          <div className="flex items-center justify-center py-24 text-neutral-400 text-sm">
            <RefreshCw className="h-5 w-5 animate-spin mr-2" />
            Loading dashboard…
          </div>
        ) : employees.length === 0 ? (
          <div className="text-center py-24 text-neutral-400 text-sm">
            No employees found.
          </div>
        ) : (
          <div className="space-y-3">
            {employees.map(emp => {
              const status = getEmployeeStatus(emp);
              const tz = meta?.timezone ?? 'Australia/Melbourne';
              const displayName = emp.first_name || emp.last_name
                ? `${emp.first_name ?? ''} ${emp.last_name ?? ''}`.trim()
                : emp.email;
              const sessions = emp.sessions ?? [];
              const hasSessions = sessions.length > 0;
              const multipleSessions = sessions.length > 1;
              const isExpanded = multipleSessions ? (expanded[emp.id] ?? true) : true;
              const editedCount = sessions.filter(s => s.edited).length;
              return (
                <div key={emp.id} className="bg-white rounded-xl border border-neutral-200 overflow-hidden hover:shadow-sm transition-shadow">
                  {/* Header */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4">
                    <div className="flex items-center gap-3 min-w-0">
                      {multipleSessions && (
                        <button
                          onClick={() => setExpanded(prev => ({ ...prev, [emp.id]: !isExpanded }))}
                          className="text-neutral-400 hover:text-neutral-700 flex-shrink-0"
                          aria-label={isExpanded ? 'Collapse sessions' : 'Expand sessions'}
                        >
                          {isExpanded
                            ? <ChevronDown className="h-4 w-4" />
                            : <ChevronRight className="h-4 w-4" />}
                        </button>
                      )}
                      <div className="min-w-0">
                        <p className="font-semibold text-neutral-900 truncate">{displayName}</p>
                        <p className="text-xs text-neutral-400 truncate">{emp.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                      {hasSessions && (
                        <span className="text-xs text-neutral-500">
                          <span className="font-semibold text-neutral-800">{formatHours(emp.total_hours_today)}</span>
                          {' · '}{sessions.length} session{sessions.length === 1 ? '' : 's'}
                          {emp.is_clocked_in && <span className="text-emerald-600"> · active</span>}
                        </span>
                      )}
                      {editedCount > 0 && (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                          <Pencil className="h-3 w-3" />
                          {editedCount} edited
                        </span>
                      )}
                      <StatusBadge status={status} />
                      {hasLocation(emp) ? (
                        <button
                          onClick={() => openLocation(emp)}
                          className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 font-medium border border-primary-200 rounded-lg px-2 py-1"
                        >
                          <MapPin className="h-3.5 w-3.5" />
                          Map
                        </button>
                      ) : null}
                    </div>
                  </div>

                  {/* Leave banner */}
                  {emp.leave_id && (
                    <div className="px-4 pb-3 -mt-1 flex items-center gap-1.5 text-blue-700 text-xs font-medium">
                      <CalendarOff className="h-3.5 w-3.5 flex-shrink-0" />
                      {LEAVE_LABELS[emp.leave_type ?? ''] ?? emp.leave_type}
                      {emp.leave_reason && <span className="text-neutral-500 font-normal">— {emp.leave_reason}</span>}
                    </div>
                  )}

                  {/* Sessions timeline */}
                  {hasSessions && isExpanded && (
                    <div className="border-t border-neutral-100 bg-neutral-50/50 divide-y divide-neutral-100">
                      {sessions.map((s, idx) => {
                        const active = s.clock_out_time == null;
                        return (
                          <div key={s.id} className="px-4 py-2.5 flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-4 text-sm">
                            <div className="flex items-center gap-2 flex-shrink-0 w-14">
                              <span className="inline-flex items-center justify-center h-5 w-5 rounded-full text-[10px] font-semibold text-neutral-500 bg-white border border-neutral-200">
                                {idx + 1}
                              </span>
                              {active && (
                                <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                              )}
                            </div>
                            <div className="flex items-center gap-1.5 text-neutral-700 min-w-[160px]">
                              <Clock className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                              <span className="font-medium tabular-nums">{formatTime(s.clock_in_time, tz)}</span>
                              <span className="text-neutral-300 mx-1">→</span>
                              {s.clock_out_time
                                ? <span className="font-medium tabular-nums">{formatTime(s.clock_out_time, tz)}</span>
                                : <span className="text-emerald-600 font-medium">still in</span>}
                            </div>
                            <div className="flex items-center gap-1.5 text-neutral-600 min-w-[90px]">
                              <span className="text-xs text-neutral-400">Duration:</span>
                              <span className="font-medium tabular-nums">
                                {formatSessionDuration(s.total_hours, s.clock_in_time, s.clock_out_time)}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 flex-1 flex-wrap">
                              {s.edited && (
                                <span
                                  className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"
                                  title={s.edit_reason ?? 'Edited'}
                                >
                                  <Pencil className="h-3 w-3" />
                                  Edited
                                  {s.edit_reason && <span className="hidden sm:inline text-amber-600 font-normal">— {s.edit_reason}</span>}
                                </span>
                              )}
                              {s.notes && (
                                <span
                                  className="inline-flex items-center gap-1 text-xs text-neutral-600 bg-white border border-neutral-200 rounded px-1.5 py-0.5"
                                  title={s.notes}
                                >
                                  <StickyNote className="h-3 w-3" />
                                  <span className="max-w-[200px] truncate">{s.notes}</span>
                                </span>
                              )}
                            </div>
                            {sessionHasLocation(s) && (
                              <button
                                onClick={() => openSessionLocation(emp, s)}
                                className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 font-medium flex-shrink-0"
                              >
                                <MapPin className="h-3 w-3" />
                                Map
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* No timesheet yet — show empty state */}
                  {!hasSessions && !emp.leave_id && (
                    <div className="px-4 pb-3 text-xs text-neutral-400">
                      Not clocked in today
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <p className="mt-4 text-center text-xs text-neutral-400">
          Auto-refreshes every 30 seconds
        </p>
      </div>

      {locationTarget && (
        <LocationModal target={locationTarget} onClose={() => setLocationTarget(null)} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Stat card helper
// ─────────────────────────────────────────────────────────────────────────────
function StatCard({
  icon,
  label,
  value,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  color: 'neutral' | 'emerald' | 'blue' | 'yellow';
}) {
  const borderMap = {
    neutral: 'border-neutral-200',
    emerald: 'border-emerald-200',
    blue: 'border-blue-200',
    yellow: 'border-yellow-200',
  };
  const valueMap = {
    neutral: 'text-neutral-800',
    emerald: 'text-emerald-700',
    blue: 'text-blue-700',
    yellow: 'text-yellow-700',
  };
  return (
    <div className={`bg-white rounded-xl border ${borderMap[color]} px-4 py-4`}>
      <div className="flex items-center justify-between mb-2">
        {icon}
        <span className={`text-2xl font-bold ${valueMap[color]}`}>{value}</span>
      </div>
      <p className="text-xs text-neutral-500 font-medium">{label}</p>
    </div>
  );
}

export default HRLiveDashboard;
